import { useDraggable } from "@dnd-kit/core";
import styled from "styled-components";

import {
  Chip,
  ChipLabel,
  isSelectableOptionSlot,
  MaOptionSlotChip,
  SegmentButton,
  Segmented,
} from "./MaOptionSlotChip";
import { MaOptionSlot } from "./maTypes";
import { MaVariantGroup } from "./maVariantGroups";

/**
 * タイムライン編集モードの下ドロワー。
 *
 * ショット内のオーディオファイルをタグ（チップ）で横並び表示する。
 * - オーディオチップはタイムラインのトラックへドラッグ & ドロップで追加できる
 * - `_a` / `_b` バリアントを持つグループはチップ内のセグメントコントロールで
 *   切り替えられる（リンク中のオーディオ / ビデオが全て切り替わる）
 * - ビデオはバリアントを持つグループのみ表示（切替専用・ドラッグ不可）
 * - `(x)` / `[x]` オプションスロットは専用チップで候補を切り替えられる
 *   （スロット由来のクリップ一式が差し替わる。ドラッグ不可。候補が 1 つのみのスロットは出さない）
 */

export interface MaEditDrawerProps {
  groups: MaVariantGroup[];
  /** グループキー → 選択中 variation_name */
  overrides: Record<string, string>;
  onSwitchVariant: (_groupKey: string, _variationName: string) => void;
  /** 編集中セグメントのオプションスロット（`(x)` / `[x]` マーカー） */
  optionSlots?: MaOptionSlot[];
  /** スロットキー → 選択中候補キー（未設定は既定候補） */
  optionSelections?: Record<string, string>;
  onSelectOption?: (_slotKey: string, _choiceKey: string) => void;
  disabled?: boolean;
}

const DrawerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 0;
  min-height: 36px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
`;

/** チップの見た目（DragOverlay でも使うため分離） */
export function MaDrawerChipVisual({
  group,
  overrides,
  onSwitchVariant,
  disabled,
  dragging,
}: {
  group: MaVariantGroup;
  overrides: Record<string, string>;
  onSwitchVariant?: (_groupKey: string, _variationName: string) => void;
  disabled?: boolean;
  dragging?: boolean;
}) {
  const isComparison = group.options.length >= 2;
  const selectedName = overrides[group.key] ?? group.options[0]?.variationName;
  const isAudio = group.kind === "audio";

  return (
    <Chip $draggable={isAudio && !disabled} $dragging={dragging}>
      <span className="material-symbols-rounded">
        {isAudio ? "music_note" : "movie"}
      </span>
      <ChipLabel className="bodytext-11 u-text-black">{group.label}</ChipLabel>
      {isComparison && (
        <Segmented onPointerDown={(e) => e.stopPropagation()}>
          {group.options.map((option) => (
            <SegmentButton
              key={option.variationName}
              type="button"
              $active={option.variationName === selectedName}
              disabled={disabled}
              onClick={() => onSwitchVariant?.(group.key, option.variationName)}
            >
              {option.letter ?? option.variationName}
            </SegmentButton>
          ))}
        </Segmented>
      )}
    </Chip>
  );
}

/** ドラッグ可能なドロワーチップ（オーディオのみドラッグ許可） */
function DrawerChip({
  group,
  overrides,
  onSwitchVariant,
  disabled,
}: {
  group: MaVariantGroup;
  overrides: Record<string, string>;
  onSwitchVariant: (_groupKey: string, _variationName: string) => void;
  disabled?: boolean;
}) {
  const isAudio = group.kind === "audio";
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `ma-drawer-${group.key}`,
    data: { group },
    disabled: !isAudio || disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ flexShrink: 0 }}
    >
      <MaDrawerChipVisual
        group={group}
        overrides={overrides}
        onSwitchVariant={onSwitchVariant}
        disabled={disabled}
        dragging={isDragging}
      />
    </div>
  );
}

const MaEditDrawer = ({
  groups,
  overrides,
  onSwitchVariant,
  optionSlots = [],
  optionSelections = {},
  onSelectOption,
  disabled,
}: MaEditDrawerProps) => {
  // オーディオ全グループ + バリアント切替可能なビデオグループ
  const visibleGroups = groups.filter(
    (g) => g.kind === "audio" || g.options.length >= 2,
  );
  const visibleSlots = optionSlots.filter(isSelectableOptionSlot);

  if (visibleGroups.length === 0 && visibleSlots.length === 0) {
    return (
      <DrawerRow>
        <span className="bodytext-11 u-text-light-gray">
          ショット内にオーディオファイルがありません
        </span>
      </DrawerRow>
    );
  }

  return (
    <DrawerRow>
      {visibleSlots.map((slot) => (
        <MaOptionSlotChip
          key={slot.key}
          slot={slot}
          selectedKey={optionSelections[slot.key] ?? slot.defaultChoiceKey}
          onSelectOption={onSelectOption}
          disabled={disabled}
        />
      ))}
      {visibleGroups.map((group) => (
        <DrawerChip
          key={group.key}
          group={group}
          overrides={overrides}
          onSwitchVariant={onSwitchVariant}
          disabled={disabled}
        />
      ))}
    </DrawerRow>
  );
};

export default MaEditDrawer;
